import React, { useRef, useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Grid, Box, Typography, CircularProgress,
    Snackbar, Alert, DialogContentText
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import DownloadIcon from '@mui/icons-material/Download';
import logoEmpresa from '../../../media/logoempresa.png';
import { eliminarTarjeta } from '../../../services/apis';
import { canvasToBMP } from '../../../utils/utils';

export const TarjetaDialog = ({ open, onClose, tarjeta, empresaNombreFallback = 'JC INFORMATICS', onDeleted }) => {
    const dialogRef = useRef(null);
    const qrWrapperRef = useRef(null);
    const [descargando, setDescargando] = useState(false);

    // Confirm dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    const nombreEmpresa = (tarjeta?.nombre_empresa && tarjeta.nombre_empresa.trim())
        ? tarjeta.nombre_empresa
        : (empresaNombreFallback || 'JC INFORMATICS');

    const nombrePersona = tarjeta?.nombre_persona || '';
    const colorPrimario = tarjeta?.color_primario || '#1976d2';
    const colorSecundario = tarjeta?.color_secundario || '#f5f5f5';
    const idNumerico = Number(tarjeta?.id);
    const esTarjetaQR = Number.isFinite(idNumerico) && idNumerico >= 10000000;

    // SOLO si es QR mostramos código
    const codigoQR = esTarjetaQR ? String(tarjeta.id) : '';

    useEffect(() => { }, [codigoQR]);

    const generarImagenDesdeQR = async (qrValueParam = null, tarjetaParam = null) => {

        const qrValue = esTarjetaQR ? (qrValueParam ?? codigoQR) : null;

        if (!nombreEmpresa || !String(nombreEmpresa).trim()) {
            setSnackbar({ open: true, message: 'Nombre de la empresa requerido', severity: 'error' });
            return;
        }

        setDescargando(true);
        try {
            let qrCanvas = null;

            if (esTarjetaQR) {
                const wrapper = qrWrapperRef.current;
                if (!wrapper) throw new Error('Referencia al QR no encontrada');
                qrCanvas = wrapper.querySelector('canvas');
                if (!qrCanvas) throw new Error('Canvas del QR no encontrado');
            }

            const canvas = document.createElement('canvas');
            canvas.width = 1004;
            canvas.height = 649;
            const ctx = canvas.getContext('2d');

            // fondo gradiente
            const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, colorPrimario);
            gradient.addColorStop(1, colorSecundario);
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // layout fijo para export
            const marginLeft = 60;
            const marginRight = 60;
            const headerTop = 20;
            const headerHeight = 80;
            const logoMaxH = 60;

            // tamaño de la cajaa QR
            const qrBoxWidth = 400;
            const qrBoxHeight = 400;
            const qrBoxX = canvas.width - marginRight - qrBoxWidth;

            // ancho disponible para texto (evitamos solapamiento con el QR)
            const gapBetweenTextAndQR = 24;
            const availableTextWidth = qrBoxX - marginLeft - gapBetweenTextAndQR;

            // Cargar logo y dibujar en el header (derecha)
            if (logoEmpresa) {
                await new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        const aspect = img.width / img.height || 1;
                        const h = Math.min(logoMaxH, img.height);
                        const w = h * aspect;
                        const logoX = canvas.width - marginRight - w;
                        const logoY = headerTop;
                        try { ctx.drawImage(img, logoX, logoY, w, h); } catch (e) { console.warn(e); }
                        resolve();
                    };
                    img.onerror = () => resolve();
                    img.src = logoEmpresa;
                });
            }

            // nombre empresa (header)
            ctx.save();
            ctx.fillStyle = 'white';
            ctx.font = 'bold 26px sans-serif';
            ctx.shadowColor = 'rgba(0,0,0,0.35)';
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            ctx.shadowBlur = 2;
            const empresaTextX = marginLeft;
            const empresaTextY = headerTop + 36;
            ctx.textAlign = 'left';
            ctx.fillText(nombreEmpresa, empresaTextX, empresaTextY);
            ctx.restore();

            // ------- Nombre persona: ajuste de tamaño y wrapping -------
            const wrapText = (ctxLocal, text, maxWidth) => {
                const words = String(text).split(/\s+/);
                const lines = [];
                let line = '';
                for (let i = 0; i < words.length; i++) {
                    const test = line ? (line + ' ' + words[i]) : words[i];
                    const metrics = ctxLocal.measureText(test);
                    if (metrics.width <= maxWidth) {
                        line = test;
                    } else {
                        if (line) {
                            lines.push(line);
                            line = words[i];
                        } else {
                            // palabra muy larga: trocear
                            let w = words[i];
                            let chunk = '';
                            for (let c = 0; c < w.length; c++) {
                                const t = chunk + w[c];
                                if (ctxLocal.measureText(t).width > maxWidth) {
                                    if (chunk) lines.push(chunk);
                                    chunk = w[c];
                                } else {
                                    chunk = t;
                                }
                            }
                            if (chunk) line = chunk;
                        }
                    }
                }
                if (line) lines.push(line);
                return lines;
            };

            let fontSize = 64;
            const minFontSize = 28;
            ctx.textAlign = 'left';
            ctx.shadowColor = 'rgba(0,0,0,0.35)';
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            ctx.shadowBlur = 2;

            let lines = [];
            while (fontSize >= minFontSize) {
                ctx.font = `bold ${fontSize}px sans-serif`;
                lines = wrapText(ctx, nombrePersona || '', availableTextWidth);
                // Si el número de líneas es razonable (<=3) o llegamos al mínimo, aceptamos
                if (lines.length <= 3 || fontSize === minFontSize) break;
                fontSize = Math.max(minFontSize, Math.floor(fontSize * 0.88));
            }

            // dibujar líneas del nombre
            ctx.save();
            ctx.fillStyle = 'white';
            ctx.font = `bold ${fontSize}px sans-serif`;
            ctx.textAlign = 'left';
            ctx.shadowColor = 'rgba(0,0,0,0.35)';
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            ctx.shadowBlur = 2;

            const personaX = marginLeft;
            const personaStartY = headerTop + headerHeight + 40;
            const lineHeight = fontSize * 1.05;
            for (let i = 0; i < lines.length; i++) {
                const y = personaStartY + (i * lineHeight);
                ctx.fillText(lines[i], personaX, y);
            }
            ctx.restore();

            // calcular posicion del QR según el espacio ocupado por el nombre
            const lastLineY = personaStartY + (Math.max(1, lines.length - 1) * lineHeight);
            const qrGapTop = 24;
            const qrBoxY = lastLineY + (fontSize * 0.6) + qrGapTop;
            const maxQrY = canvas.height - qrBoxHeight - 60;
            const finalQrBoxY = Math.min(qrBoxY, maxQrY);

            if (esTarjetaQR && qrCanvas) {
                // dibujar caja blanca del QR
                ctx.save();
                ctx.shadowColor = 'rgba(0,0,0,0.25)';
                ctx.shadowBlur = 8;
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 4;
                const r = 10;
                ctx.fillStyle = 'white';
                ctx.beginPath();
                ctx.moveTo(qrBoxX + r, finalQrBoxY);
                ctx.lineTo(qrBoxX + qrBoxWidth - r, finalQrBoxY);
                ctx.quadraticCurveTo(qrBoxX + qrBoxWidth, finalQrBoxY, qrBoxX + qrBoxWidth, finalQrBoxY + r);
                ctx.lineTo(qrBoxX + qrBoxWidth, finalQrBoxY + qrBoxHeight - r);
                ctx.quadraticCurveTo(qrBoxX + qrBoxWidth, finalQrBoxY + qrBoxHeight, qrBoxX + qrBoxWidth - r, finalQrBoxY + qrBoxHeight);
                ctx.lineTo(qrBoxX + r, finalQrBoxY + qrBoxHeight);
                ctx.quadraticCurveTo(qrBoxX, finalQrBoxY + qrBoxHeight, qrBoxX, finalQrBoxY + qrBoxHeight - r);
                ctx.lineTo(qrBoxX, finalQrBoxY + r);
                ctx.quadraticCurveTo(qrBoxX, finalQrBoxY, qrBoxX + r, finalQrBoxY);
                ctx.closePath();
                ctx.fill();
                ctx.restore();

                // dibujar QR
                const innerPadding = 8;
                const qrInnerSize = qrBoxWidth - innerPadding * 2;
                const qrInnerX = qrBoxX + innerPadding;
                const qrInnerY = finalQrBoxY + innerPadding;
                ctx.drawImage(qrCanvas, qrInnerX, qrInnerY, qrInnerSize, qrInnerSize);
            }

            // footer codigo
            ctx.save();
            ctx.fillStyle = 'rgba(255,255,255,0.95)';
            ctx.font = '16px monospace';
            ctx.textAlign = 'center';
            ctx.fillText(
                esTarjetaQR
                    ? `Código: ${qrValue}`
                    : 'Identificación por RFID',
                canvas.width / 2,
                canvas.height - 36
            );
            ctx.restore();

            // export
            // Exportar en formato BMP
            // -------- Exportar como BMP de 24 BPP --------
            try {
                const bmpBlob = canvasToBMP(canvas); // <- tu función ya existente

                const link = document.createElement('a');
                const safeEmpresa = (nombreEmpresa || 'empresa').replace(/\s+/g, '-');
                const filename = `tarjeta-${safeEmpresa}-${tarjetaParam?.id ?? tarjeta?.id ?? 'n'}.bmp`;

                link.href = URL.createObjectURL(bmpBlob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();

                setTimeout(() => URL.revokeObjectURL(link.href), 200);
                document.body.removeChild(link);

                setSnackbar({ open: true, message: 'Tarjeta BMP generada correctamente', severity: 'success' });

            } catch (e) {
                console.error("Error generando BMP:", e);
                setSnackbar({ open: true, message: 'No se pudo generar la tarjeta BMP', severity: 'error' });
            }



            setSnackbar({ open: true, message: 'Tarjeta generada y descargada', severity: 'success' });
        } catch (err) {
            console.error('generarImagenDesdeQR error:', err);
            setSnackbar({ open: true, message: 'Error al generar la imagen de la tarjeta', severity: 'error' });
        } finally {
            setDescargando(false);
        }
    };




    const handleEliminar = async () => {
        if (!tarjeta) {
            setSnackbar({ open: true, message: 'No se encontró la tarjeta a eliminar.', severity: 'error' });
            return;
        }

        const idUsuario = tarjeta.id_usuario ?? null;
        const idTarjeta = tarjeta.id ?? null;

        // sanity check
        if (!idUsuario && !idTarjeta) {
            console.error('handleEliminar: falta id_usuario e id en objeto tarjeta', tarjeta);
            setSnackbar({ open: true, message: 'No se pudo identificar la tarjeta (falta id). Revisa la consola.', severity: 'error' });
            return;
        }

        setDescargando(true);
        try {
            // payload en "data" para mantener la misma estructura que crearTarjeta
            const payload = idUsuario ? { id_usuario: idUsuario } : { id: idTarjeta };
            console.log('eliminarTarjeta payload:', payload);

            const resp = await eliminarTarjeta(payload); // espera { success: true/false, ... }

            console.log('eliminarTarjeta resp:', resp);

            if (resp && resp.success) {
                setSnackbar({ open: true, message: resp.message || 'Tarjeta eliminada correctamente', severity: 'success' });

                // notificar al padre (si se pasó) con la tarjeta eliminada
                if (typeof onDeleted == 'function') {
                    try { onDeleted(resp.tarjeta ?? { id: resp.id, id_usuario: resp.id_usuario }); } catch (e) { /* ignore */ }
                }

                // cerrar diálogo tras breve espera para que el snackbar se vea
                setTimeout(() => onClose(true), 700);
            } else {
                setSnackbar({ open: true, message: resp?.message || 'No se pudo eliminar la tarjeta', severity: 'error' });
            }
        } catch (err) {
            console.error('handleEliminar error:', err);
            setSnackbar({ open: true, message: 'Error al eliminar la tarjeta (revisa la consola)', severity: 'error' });
        } finally {
            setDescargando(false);
            setConfirmOpen(false);
        }
    };

    return (
        <>
            <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Tarjeta: {tarjeta ? (tarjeta.nombre_persona || tarjeta.id_usuario || tarjeta.id) : '—'}</span>
                    <Box />
                </DialogTitle>

                <DialogContent dividers>
                    {/* Contenedor principal: fondo con gradiente — usamos Grid para estructura responsive */}
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: { xs: 1, sm: 2, md: 3 } }}>
                        <Grid container ref={dialogRef}
                            sx={{
                                maxWidth: 900,
                                width: { xs: '100%', sm: 700, md: 800 },
                                borderRadius: 2,
                                p: { xs: 2, md: 3 },
                                boxShadow: 3,
                                background: `linear-gradient(135deg, ${colorPrimario} 0%, ${colorSecundario} 100%)`
                            }}
                            spacing={2}
                            alignItems="stretch"
                        >
                            <Grid item xs={12} container alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
                                <Grid item>
                                    <Typography sx={{
                                        fontWeight: 'bold',
                                        color: 'white',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                        fontSize: { xs: '1rem', sm: '1.4rem', md: '2rem' }
                                    }}>
                                        {nombreEmpresa}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Box component="img" src={logoEmpresa} alt="Logo" sx={{
                                        height: { xs: 28, sm: 44, md: 72 },
                                        width: 'auto',
                                        objectFit: 'contain',
                                        filter: 'brightness(0) invert(1)'
                                    }} onError={(e) => e.target.style.display = 'none'} />
                                </Grid>
                            </Grid>

                            <Grid item xs={12} container spacing={2} alignItems="flex-start">
                                <Grid item xs={12} md={7} sx={{ pr: { md: 1 } }}>
                                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                                        {nombrePersona && (
                                            <Typography sx={{
                                                fontWeight: 'bold',
                                                color: 'white',
                                                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                                fontSize: { xs: '1.4rem', sm: '2rem', md: '2.8rem' },
                                                lineHeight: 1.05
                                            }}>
                                                {nombrePersona}
                                            </Typography>
                                        )}
                                        <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                            Tarjeta de identificación
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={5} container justifyContent={{ xs: 'center', md: 'flex-end' }}>
                                    <Box sx={{
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: { xs: 160, sm: 180, md: 220 },
                                        height: { xs: 160, sm: 180, md: 220 },
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                        mt: { xs: 2, md: 4 }
                                    }}>
                                        {esTarjetaQR ? (
                                            <QRCodeCanvas value={codigoQR} includeMargin={false} level="L" size={240} />
                                        ) : (
                                            <Typography variant="caption">Identificación por RFID</Typography>
                                        )}
                                        <Typography variant="caption" sx={{ mt: 1, fontWeight: 'bold' }}>ESCANEAR PARA FICHAR</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 1.5, textAlign: 'center' }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.75rem', md: '0.9rem' }, fontFamily: 'monospace' }}>
                                        Código: {codigoQR ? (codigoQR.length > 40 ? `${codigoQR.substring(0, 40)}...` : codigoQR) : 'XXXX-XXXX-XXXX'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => generarImagenDesdeQR(null, tarjeta)} variant="contained" startIcon={<DownloadIcon />} disabled={!tarjeta || descargando}>
                        {descargando ? <CircularProgress size={18} color="inherit" /> : 'Descargar tarjeta'}
                    </Button>

                    <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)} disabled={descargando || !tarjeta}>
                        {descargando ? <CircularProgress size={18} color="inherit" /> : 'Eliminar tarjeta'}
                    </Button>

                    <Button onClick={onClose}>Cerrar</Button>
                </DialogActions>

                {/* QR oculto para capturar canvas */}
                {esTarjetaQR && (
                    <div
                        style={{ position: 'absolute', left: -9999, top: -9999, width: 1, height: 1, overflow: 'hidden' }}
                        ref={qrWrapperRef}
                    >
                        <QRCodeCanvas value={codigoQR} size={256} includeMargin={false} level="L" />
                    </div>
                )}
            </Dialog>

            {/* Confirm dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que quieres eliminar esta tarjeta? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={descargando}>Cancelar</Button>
                    <Button
                        onClick={handleEliminar}
                        color="error"
                        variant="contained"
                        disabled={descargando}
                        startIcon={descargando ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {descargando ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para feedback bonito */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default TarjetaDialog;
